import {} from '@views/pages/Reporting/Reports/hooks/useReportingTable.tsx';
import { useState } from 'react';
import {
  useDeleteReportFolderFavoriteMutation,
  useDeleteReportFolderMutation,
  useMakeReportFolderFavoriteMutation,
  useReportFoldersQuery,
} from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useReportFoldersContext } from '@views/pages/Reporting/ReportFolders/context';
import { ReportFoldersTableEntry } from '@views/pages/Reporting/ReportFolders/hooks/useReportFoldersTable.ts';
import { ReportFoldersPageTableProps } from '@views/pages/Reporting/ReportFolders/components/ReportFoldersPageTable.tsx';

export const useReportFoldersPageTable = ({
  query,
}: ReportFoldersPageTableProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reportIdToDelete, setReportIdToDelete] = useState('');
  const [reportIdToFavorite, setReportIdToFavorite] = useState('');
  const { filters, handleFilterChange, isInitialFilters } =
    useReportFoldersContext();

  const {
    data = {
      folders: [],
      total: 0,
    },
    isPending,
  } = useReportFoldersQuery({
    ...filters,
    ...(query === 'favorites_only' && { favorites_only: true }),
    ...(query === 'created_by_me' && { created_by_me: true }),
    ...(query === 'recently_deleted' && { recently_deleted: true }),
    ...(query === 'recently_viewed' && { recently_viewed: true }),
  });

  const {
    mutateAsync: deleteReportFolderMutation,
    isPending: deleteReportFolderIsLoading,
  } = useDeleteReportFolderMutation();

  const {
    mutateAsync: makeReportFolderFavoriteMutation,
    isPending: makeReportFolderFavoriteIsPending,
  } = useMakeReportFolderFavoriteMutation();

  const {
    mutateAsync: deleteReportFolderFavoriteMutation,
    isPending: deleteReportFolderFavoriteIsPending,
  } = useDeleteReportFolderFavoriteMutation();

  const handleOpenDeleteModal = (id: string) => {
    setIsDeleteModalOpen(true);
    setReportIdToDelete(id);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setReportIdToDelete('');
  };

  const handleDeleteReportFolder = async () => {
    try {
      await deleteReportFolderMutation(reportIdToDelete);

      toast.success('Report folder deleted successfully.');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleMakeReportFolderFavorite = async (
    id: string,
    isFavorite: boolean,
  ) => {
    setReportIdToFavorite(id);
    const action = isFavorite
      ? deleteReportFolderFavoriteMutation
      : makeReportFolderFavoriteMutation;

    try {
      await action(id);

      toast.success(
        isFavorite
          ? 'Report deleted from favorite successfully'
          : 'Report set as favorite successfully.',
      );
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setReportIdToFavorite('');
    }
  };

  const entries: ReportFoldersTableEntry[] = data.folders.map(item => {
    return {
      ...item,
      created_by: {
        id: item.created_by?.id || '-',
        name: item.created_by?.name || '-',
      },
      organization: {
        id: item.organization?.id || '-',
        name: item.organization?.name || '-',
      },
    };
  });

  return {
    filters,
    entries,
    handleFilterChange,
    totalCount: data.total,
    loading: isPending,
    isDeleteModalOpen,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleDeleteReportFolder,
    deleteReportFolderIsLoading,
    makeReportFolderFavoriteIsPending:
      makeReportFolderFavoriteIsPending || deleteReportFolderFavoriteIsPending,
    handleMakeReportFolderFavorite,
    reportIdToFavorite,
    isInitialFilters,
  };
};
